import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { looksLikeMedicationList } from "../utils/medicationList";
import {
  medicationInputStyles,
  medicationPrimaryButtonStyles,
  medicationSecondaryButtonStyles,
} from "./medicationReviewStyles";

const unavailableMessage =
  "We couldn’t separate the entire list. Go back and add one medication at a time. Your original text is preserved above.";

// rows are the separate medications the extractor found in the original entry
// (see reviewableMedicationRows). null means it could not separate a name that
// reads like a list, so the patient goes back or keeps one combination product.
export default function MedicationListReview({
  entry,
  rows,
  onSave,
  onBack,
  onKeepSingle,
}) {
  const [entries, setEntries] = useState(rows || []);
  const error = rows ? "" : unavailableMessage;

  const update = (index, field, value) =>
    setEntries((current) =>
      current.map((item, i) => {
        if (i !== index) return item;
        return {
          ...item,
          [field]: value,
          // Editing a suggested name invalidates its catalog/product match.
          ...(field === "name"
            ? { rxCUI: "", ndc: "", productStrength: "", form: "", route: "" }
            : {}),
        };
      }),
    );
  const valid =
    !error &&
    entries.length > 0 &&
    entries.every(
      (item) =>
        item.name?.trim() &&
        !looksLikeMedicationList(item.name) &&
        ["name", "dose", "frequency", "productStrength", "form", "route"].every(
          (field) => (item[field] || "").trim().length <= 300,
        ),
    );

  return (
    <>
      <Box sx={{ display: "grid", gap: 2, px: 3, pb: 3 }}>
        <Typography>
          This looks like more than one medication. Check that each medication
          or supplement has its own row, and that nothing is missing.
        </Typography>
        <Box sx={{ p: 2, backgroundColor: "#faf9fc", borderRadius: "6px" }}>
          <Typography sx={{ fontWeight: 600 }}>Your original entry</Typography>
          <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {entry.name}
          </Typography>
          {(entry.dose || entry.frequency) && (
            <Typography sx={{ mt: 1 }}>
              You also entered:{" "}
              {[entry.dose, entry.frequency].filter(Boolean).join(" · ")}. Add
              these details to the correct medication below.
            </Typography>
          )}
        </Box>
        {error && <Alert severity="warning">{error}</Alert>}
        {entries.map((item, index) => (
          <Box
            component="fieldset"
            key={index}
            sx={{
              display: "grid",
              gap: 2,
              m: 0,
              p: 2,
              border: "1px solid #e9e6ef",
            }}
          >
            <legend>Medication {index + 1}</legend>
            {item.taking === false && (
              <Typography>Reported as no longer taking</Typography>
            )}
            {[
              ["name", "Medication or supplement name"],
              ["dose", "Dose (optional)"],
              ["frequency", "How often (optional)"],
            ].map(([field, label]) => (
              <TextField
                key={field}
                id={`medication-list-${index}-${field}`}
                label={label}
                value={item[field] || ""}
                onChange={(event) => update(index, field, event.target.value)}
                inputProps={{ maxLength: 300 }}
                required={field === "name"}
                error={field === "name" && looksLikeMedicationList(item.name)}
                helperText={
                  field === "name" && looksLikeMedicationList(item.name)
                    ? "Enter one medication per row."
                    : undefined
                }
                sx={medicationInputStyles}
              />
            ))}
            {[item.productStrength, item.form, item.route].filter(Boolean)
              .length > 0 && (
              <Typography sx={{ fontSize: "14px" }}>
                {[
                  item.productStrength && `Strength: ${item.productStrength}`,
                  item.form,
                  item.route,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            )}
            <Button
              type="button"
              onClick={() =>
                setEntries((current) => current.filter((_, i) => i !== index))
              }
              aria-label={`Remove medication ${index + 1}`}
              sx={medicationSecondaryButtonStyles}
            >
              Remove
            </Button>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          mt: "auto",
          p: 3,
          borderTop: "1px solid #e9e6ef",
        }}
      >
        {!error && (
          <Button
            type="button"
            variant="contained"
            disableElevation
            disabled={!valid}
            onClick={() => {
              if (valid) onSave(entries);
            }}
            sx={medicationPrimaryButtonStyles}
          >
            Use reviewed list
          </Button>
        )}
        <Button
          type="button"
          onClick={onBack}
          sx={medicationSecondaryButtonStyles}
        >
          Back to original entry
        </Button>
        <Button
          type="button"
          disabled={entry.name.length > 300}
          onClick={onKeepSingle}
          sx={medicationSecondaryButtonStyles}
        >
          This is one combination product
        </Button>
      </Box>
    </>
  );
}
